/**
 * CalendarDayView
 * Daily calendar view with hourly timeline showing events and tasks
 */

import React from 'react'
import { Clock } from 'lucide-react'
import type { CalendarItem } from '../../types/calendar'
import { CalendarEventCard } from './CalendarEventCard'
import { CalendarTaskCard } from './CalendarTaskCard'

interface CalendarDayViewProps {
  items: CalendarItem[]
  date: Date
  onItemClick?: (item: CalendarItem) => void
}

interface TimeSlot {
  hour: number
  label: string
  items: CalendarItem[]
}

/**
 * CalendarDayView Component
 */
export const CalendarDayView: React.FC<CalendarDayViewProps> = ({
  items,
  date,
  onItemClick
}) => {
  // Filter items for this specific day
  const dayItems = items.filter(item => {
    const itemDate = new Date(item.date)
    itemDate.setHours(0, 0, 0, 0)
    const targetDate = new Date(date)
    targetDate.setHours(0, 0, 0, 0)
    return itemDate.getTime() === targetDate.getTime()
  })

  // Separate items by time
  const timedEvents = dayItems.filter(item => item.type === 'event' && item.startTime)
  const allDayItems = dayItems.filter(item => item.type === 'event' && !item.startTime)
  const tasks = dayItems.filter(item => item.type === 'task')

  // Generate time slots (8 AM to 8 PM)
  const generateTimeSlots = (): TimeSlot[] => {
    const slots: TimeSlot[] = []

    for (let hour = 8; hour <= 20; hour++) {
      const hourStr = hour.toString().padStart(2, '0')
      slots.push({
        hour,
        label: `${hourStr}:00`,
        items: []
      })
    }

    return slots
  }

  // Assign timed events to slots
  const assignEventsToSlots = (slots: TimeSlot[]): TimeSlot[] => {
    timedEvents.forEach(event => {
      if (event.startTime) {
        const eventHour = parseInt(event.startTime.split(':')[0])
        const slot = slots.find(s => s.hour === eventHour)
        if (slot) {
          slot.items.push(event)
        }
      }
    })

    return slots
  }

  const timeSlots = assignEventsToSlots(generateTimeSlots())

  // Format date display
  const dateDisplay = date.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })

  return (
    <div className="min-w-0 rounded-lg border bg-card">
      {/* Day Header */}
      <div className="border-b p-4">
        <h2 className="text-lg font-semibold capitalize">
          {dateDisplay}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {dayItems.length} {dayItems.length === 1 ? 'item' : 'itens'} neste dia
        </p>
      </div>

      {/* All-Day Items */}
      {allDayItems.length > 0 && (
        <div className="border-b bg-muted/40 p-4">
          <div className="text-xs font-medium text-muted-foreground mb-2">
            Dia Inteiro
          </div>
          <div className="space-y-2">
            {allDayItems.map(item => (
              <div
                key={`${item.type}-${item.id}`}
                onClick={() => onItemClick?.(item)}
              >
                <CalendarEventCard item={item} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tasks Section */}
      {tasks.length > 0 && (
        <div className="border-b bg-accent/30 p-4">
          <div className="text-xs font-medium text-muted-foreground mb-2">
            Tarefas do Dia
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {tasks.map(item => (
              <div
                key={`${item.type}-${item.id}`}
                onClick={() => onItemClick?.(item)}
              >
                <CalendarTaskCard item={item} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      <div className="overflow-y-auto max-h-[600px]">
        {timeSlots.map((slot) => (
          <div
            key={slot.hour}
            className="flex border-b transition-colors last:border-b-0 hover:bg-muted/60"
          >
            {/* Time Label */}
            <div className="w-20 shrink-0 border-r p-3 text-right">
              <div className="flex items-center justify-end gap-1.5 text-sm text-muted-foreground">
                <Clock className="size-3" aria-hidden="true" />
                <span>{slot.label}</span>
              </div>
            </div>

            {/* Events */}
            <div className="min-w-0 flex-1 p-3">
              {slot.items.length === 0 ? (
                <div className="h-12" /> // Empty space
              ) : (
                <div className="space-y-2">
                  {slot.items.map(item => (
                    <div
                      key={`${item.type}-${item.id}`}
                      onClick={() => onItemClick?.(item)}
                    >
                      <CalendarEventCard item={item} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {dayItems.length === 0 && (
        <div className="p-12 text-center">
          <Clock className="mx-auto mb-3 size-10 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">Nenhum evento ou tarefa neste dia</p>
        </div>
      )}
    </div>
  )
}
